import axios from "axios";
import debounce from "lodash/debounce";
import ally from 'ally.js';
import { uuidv4 } from '../../_Global/js/utils';

interface FunnelbackSuggestion {
  key: string;
  disp: string;
  disp_t: string;
  wt: string;
  cat: string;
  cat_t: string;
  action: string;
  action_t: string;
}

type SuggestionFocusDirection  = "up" | "down";

function FunnelbackAutocomplete(component: HTMLFormElement): void {
  // Funnelback configuration
  const requestEndpoint = "https://dxp-au-search.funnelback.squiz.cloud/s/suggest.json";
  const defaultRequestParameters: string[] = [
    'fmt=json%2B%2B',
    'alpha=0.5',
    'profile=_default_preview',
    'show=10'
  ];

  let inputKeysHandle;
  let suggestionsKeysHandle;
  let suggestionsAreVisible = false;
  let suggestionsAreAvailable = false;
  let inputIsFocussed = false;
  let currentFocussedSuggestion: Element | null = null;

  /**
   * The minimum number of characters the user must
   * enter before search suggestions are requested.
   */
  const suggestionTriggerThreshold = 2;

  /**
   * The delay (in ms) after the user enters text into
   * the search input before looking up suggestions.
   */
  const suggestionLookupDelay = 250;

  // Get the required configuration from the component markup
  const collectionId = component.querySelector('[data-role="predictive-search-collection-id"]')?.getAttribute('value');
  const searchInputElement: HTMLInputElement | null = component.querySelector('[data-role="predictive-search-input"]');
  const searchResetElement: HTMLButtonElement | null = component.querySelector('[data-role="predictive-search-reset"]');
  const suggestionsListElement = component.querySelector('[data-role="predictive-search-list"]');

  /**
   * Controller for cancelling pending XHR requests.
   *
   * @source https://www.npmjs.com/package/axios#cancellation
   */
  let funnelbackSuggestionRequestController: AbortController | null = null;

  // Check that all component configuration is provided
  if (
    !collectionId ||
    !searchInputElement ||
    !searchResetElement ||
    !suggestionsListElement
  ) {
    console.log('Error: Predictive search is missing required configuration. Check:', component);
    return;
  }

  /**
   * Respond to the user's input into the text field
   */
  function onUserTextInput(evt) {
    const textInput: string = evt.target.value;

    if (textInput.length > 0) {
      toggleInputReset(true);
    } else {
      toggleInputReset(false);
    }

    if (textInput.length >= suggestionTriggerThreshold) {
      getSearchSuggestions(textInput);
    } else {
      cancelSuggestionsRequest();
      toggleSearchSuggestions(false, true);
    }
  }

  /**
   * Respond to the text input gaining focus
   */
  function onInputFocus() {
    inputIsFocussed = true;

    if (suggestionsAreAvailable) {
      toggleSearchSuggestions(true);
    }

    // React to escape/up/down key presses as mandated by ARIA Practices
    inputKeysHandle = ally.when.key({
      escape: (evt: KeyboardEvent) => {
        onEscapeKeyPress(evt);
      },
      up: (evt: KeyboardEvent) => {
        if (suggestionsAreAvailable) {
          toggleSearchSuggestions(true);
        }

        if (suggestionsAreVisible) {
          // Prevent viewport movement
          evt.preventDefault();

          // Update the suggestions
          updateSelectedSuggestion('up');
        }
      },
      down: (evt: KeyboardEvent) => {
        if (suggestionsAreAvailable) {
          toggleSearchSuggestions(true);
        }

        if (suggestionsAreVisible) {
          // Prevent viewport movement
          evt.preventDefault();

          // Update the suggestions
          updateSelectedSuggestion('down');
        }
      }
    });
  }

  /**
   * Respond to the text input losing focus
   */
  function onInputBlur() {
    inputIsFocussed = false;
    toggleSearchSuggestions(false);

    // Undo listening to key presses
    inputKeysHandle.disengage();
  }

  /**
   * Respond to clicks on the search reset button
   */
  function onSearchResetClick(evt: Event) {
    evt.preventDefault();
    resetSearch();
  }

  /**
   * Respond to clicks on the search suggestions
   */
  function onSuggestionClick(evt: Event) {
    const clickedElement = evt.target as Element;
    const suggestedValue = clickedElement.getAttribute('data-search-value');

    if (suggestedValue) {
      // User clicked a suggestion
      // Set the clicked value in the search field
      replaceSearchInputValue(suggestedValue);

      // Hide and clear the search suggestions
      toggleSearchSuggestions(false, true);

      // Set focus on the text input
      ally.element.focus(searchInputElement);

      // Note: Disabled auto-submit for it to be consistent with ARIA spec
      // component.submit();
    }
  }

  /**
   * Respond to ESC key presses.
   * Aria features.
   * Close the suggestions if they are open.
   * Clear the input if any text entered
   */
  function onEscapeKeyPress(evt: KeyboardEvent) {
    // Prevent the viewport from jumping to top of page
    evt.preventDefault();
    evt.stopPropagation();

    if (inputIsFocussed && !suggestionsAreVisible) {
      resetSearch();
    }

    if (suggestionsAreVisible) {
      toggleSearchSuggestions(false);
    }
  }

  /**
   * A complete reset of the search autocomplete state.
   */
  function resetSearch() {
    clearSearchInput();
    cancelSuggestionsRequest();
    toggleSearchSuggestions(false, true);
    toggleInputReset(false);
  }

  /**
   * Replace the current value in the text input
   *
   * @param searchTerm
   * The value to insert into the text input
   */
  function replaceSearchInputValue(searchTerm: string) {
    if (searchInputElement) {
      searchInputElement.value = searchTerm;
    }
  }

  /**
   * Remove any current value in the text input
   */
  function clearSearchInput() {
    if (searchInputElement) {
      searchInputElement.value = '';
    }
  }

  /**
   * Cancel any pending request for search suggestions
   */
  function cancelSuggestionsRequest() {
    if (funnelbackSuggestionRequestController !== null) {
      funnelbackSuggestionRequestController.abort();
    }
  }

  /**
   * Request search suggestions from Funnelback.
   *
   * @param partialQuery
   * The search query input by the user
   */
  function getSearchSuggestions(partialQuery: string) {
    // Combine all the query parameters
    const fullQueryString: string[] = [
      ...defaultRequestParameters,
      `collection=${collectionId}`,
      `partial_query=${partialQuery}`
    ];

    cancelSuggestionsRequest();

    // Create a new controller instance
    funnelbackSuggestionRequestController = new AbortController();

    axios
        .get(`${requestEndpoint}?${fullQueryString.join('&')}`, {
            signal: funnelbackSuggestionRequestController.signal,
        })
        .then((response) => {
          const searchSuggestions: FunnelbackSuggestion[] = response.data;

          // Clear the request controller
          funnelbackSuggestionRequestController = null;

          // Update the page
          updateSearchSuggestions(searchSuggestions);
        })
        .catch((error) => {
            if (error.code === 'ERR_CANCELED') {
                // The request was cancelled, do nothing.
                return;
            }
        });
  }

  /**
   * Generate the markup for the search suggestions
   * and replace the existing options.
   */
  function updateSearchSuggestions(suggestions: FunnelbackSuggestion[]) {
    const suggestionElementContainer = document.createElement("div");

    for (let i = 0, j = suggestions.length; i < j; ++i) {
      // Create the list item
      const listItem = document.createElement("li");

      listItem.setAttribute('data-aria-id', uuidv4());
      listItem.classList.add("body-size-sm", "custom-dropdown__list-item");
      listItem.setAttribute('data-search-value', suggestions[i].disp);
      listItem.textContent = suggestions[i].disp;

      // Add to the temporary container
      suggestionElementContainer.appendChild(listItem);
    }

    if (suggestions.length !== 0) {
      // Replace existing suggestions
      suggestionsListElement?.replaceChildren(...suggestionElementContainer.children);

      suggestionsAreAvailable = true;

      toggleSearchSuggestions(true);
    } else {
      toggleSearchSuggestions(false, true);
    }
  }

  /**
   * Show or hide the search suggestions dropdown menu.
   * Optionally remove any current suggestions.
   */
  function toggleSearchSuggestions(setAsVisible: Boolean, clearSuggestions: Boolean = false) {

    if (setAsVisible) {
      // Set the input as expanded
      searchInputElement?.setAttribute('aria-expanded', 'true');

      // Hide the suggestions
      suggestionsListElement?.classList.add('custom-dropdown__list--is-active');

      suggestionsAreVisible = true;
    } else {
      // Set the input as expanded
      searchInputElement?.setAttribute('aria-expanded', 'false');

      // Hide the suggestions
      suggestionsListElement?.classList.remove('custom-dropdown__list--is-active');

      suggestionsAreVisible = false;

      resetSelectedSuggestion();
    }

    if (clearSuggestions) {
      suggestionsListElement?.replaceChildren();
      suggestionsAreAvailable = false;
    }
  }

  /**
   * Set the appropriate search suggestion to be visually
   * focussed, based on the provided direction.
   *
   * @param direction
   * The direction to move the visual focus in the suggestion list.
   */
  function updateSelectedSuggestion(direction: SuggestionFocusDirection) {
    let nextFocussedSuggestion: Element | null = null;
    let previousSuggestion: Element | null = null;

    if (!currentFocussedSuggestion) {
      // No suggestion is currently focussed
      if (direction === 'up') {
        // Focus the last item in the suggestions list
        currentFocussedSuggestion = suggestionsListElement?.children.item(suggestionsListElement.children.length - 1) as Element;
      }

      if (direction === 'down') {
        // Focus the first item in the suggestions list
        currentFocussedSuggestion = suggestionsListElement?.children.item(0) as Element;
      }

      // React to key presses as mandated by ARIA Practices
      suggestionsKeysHandle = ally.when.key({
        enter: (evt: KeyboardEvent) => {
          // Prevent the automatic form submission
          evt.preventDefault();

          // Update the search text field
          replaceSearchInputValue(currentFocussedSuggestion?.getAttribute('data-search-value') || "");

          // Hide and clear the search suggestions
          toggleSearchSuggestions(false, true);
        },
        up: (evt: KeyboardEvent) => {
          // Prevent viewport movement
          evt.preventDefault();

          // Update the suggestions
          updateSelectedSuggestion('up');
        },
        down: (evt: KeyboardEvent) => {
          // Prevent viewport movement
          evt.preventDefault();

          // Update the suggestions
          updateSelectedSuggestion('down');
        },
        left: resetSelectedSuggestion,
        right: resetSelectedSuggestion
      });
    } else {
      // Move the focus up or down, accordingly

      // Capture a reference to the currently focussed element
      previousSuggestion = currentFocussedSuggestion;

      if (direction === 'up') {
        // Get the previous sibling
        nextFocussedSuggestion = currentFocussedSuggestion.previousElementSibling;

        if (nextFocussedSuggestion) {
          // There is another option
          currentFocussedSuggestion = nextFocussedSuggestion;
        } else {
          // We have reached the start of the list, skip to the end
          currentFocussedSuggestion = suggestionsListElement?.children.item(suggestionsListElement.children.length - 1) as Element;
        }
      }

      if (direction === 'down') {
        // Get the next sibling
        nextFocussedSuggestion = currentFocussedSuggestion.nextElementSibling;

        if (nextFocussedSuggestion) {
          // There is another option
          currentFocussedSuggestion = nextFocussedSuggestion;
        } else {
          // We have reached the end of the list, loop back to the beginning
          currentFocussedSuggestion = suggestionsListElement?.children.item(0) as Element;
        }
      }

      // Update the previous suggestion "state"
      // Remove the styling
      previousSuggestion.classList.remove('is-visually-focussed');

      // Remove the attribute from the item
      previousSuggestion.removeAttribute('aria-selected');
    }

    if (currentFocussedSuggestion) {
      // Add the styling
      currentFocussedSuggestion?.classList.add('is-visually-focussed');

      // Set the attribute on the item
      currentFocussedSuggestion?.setAttribute('aria-selected', 'true');

      // Set the attribute on the input
      searchInputElement?.setAttribute('aria-activedescendant', currentFocussedSuggestion?.getAttribute('data-aria-id') || "");
    }
  }

  /**
   * Remove the accessible styling and attributes from the
   * search suggestions
   */
  function resetSelectedSuggestion() {
    if (currentFocussedSuggestion) {
      // Remove the styling
      currentFocussedSuggestion?.classList.remove('is-visually-focussed');

      // Remove the attribute from the item
      currentFocussedSuggestion?.removeAttribute('aria-selected');

      // Remove the attribute from the input
      searchInputElement?.removeAttribute('aria-activedescendant');

      currentFocussedSuggestion = null;

      // Undo listening to key presses
      suggestionsKeysHandle.disengage();
    }
  }

  /**
   * Show or hide the clear/reset search button.
   */
  function toggleInputReset(setAsVisible: Boolean) {
    if (setAsVisible) {
      searchResetElement?.classList.add('open-search--show-clear');
    } else {
      searchResetElement?.classList.remove('open-search--show-clear');
    }
  }

  // Listen for search query inputs
  searchInputElement.addEventListener('input',
    debounce(
      onUserTextInput,
      suggestionLookupDelay,
      {
          leading: false,
          trailing: true
      }
    )
  );

  // Listen for search query focus
  searchInputElement.addEventListener('focus', onInputFocus);

  // Listen for search query loss of focus
  searchInputElement.addEventListener('blur',
    debounce(
      onInputBlur,
      250,
      {
          leading: false,
          trailing: true
      }
    )
  );

  /**
   * Listen for clicks/taps on the document.
   *
   * If the user clicked on anything other than
   * the component, close the search suggestions.
   *
   * This bit of functionality is specific
   * for iOS devices that don't seem to consider
   * tapping elsewhere on the page a change to
   * the focussed element.
   *
   * On other devices, the 'blur' event on the
   * search input is enough to trigger the
   * desired behaviour.
   */
  document.addEventListener('click', (evt) => {
    const isComponentDescendant = component.contains(evt.target as Node);

    if (!isComponentDescendant && suggestionsAreVisible) {
      toggleSearchSuggestions(false);
    }
  });

  // Listen for reset actions
  searchResetElement.addEventListener('click', onSearchResetClick);

  // Listen for clicks on the suggestions menu
  suggestionsListElement.addEventListener('click', onSuggestionClick);

};

export default FunnelbackAutocomplete;